import { useEffect, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import type { UserManager } from "oidc-client-ts";
import { asError, eventBus } from "@optimyl/events";
import { clearReturnPath, readReturnPath } from "./identityRedirect";

export interface SignInCallbackProps {
  getUserManager: () => UserManager;
  /** Where to go when no page was remembered. */
  homePath: string;
  /** What the app shows while the code is exchanged. */
  busyView: ReactNode;
  /** What the app shows when the exchange fails, given the failure's message. */
  errorView: (message: string) => ReactNode;
}

/**
 * The page the identity server sends the browser back to (`/auth/callback`): finishes the sign-in, then goes to the page
 * remembered before it started, else home. A callback handled already (Back, or a session cleared mid-flow) is not an
 * error: a live session goes on, anything else starts over at the root. A failure is announced to screen readers as
 * well as shown. Built by createAuth.
 */
export function SignInCallback({ getUserManager, homePath, busyView, errorView }: SignInCallbackProps) {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const handledRef = useRef(false);

  useEffect(() => {
    // Once, whatever React StrictMode does with the effect.
    if (handledRef.current) return;
    handledRef.current = true;

    const userManager = getUserManager();
    const returnPath = readReturnPath();
    userManager
      .signinRedirectCallback()
      .then(() => {
        clearReturnPath();
        navigate(returnPath || homePath, { replace: true });
      })
      .catch(async (err: unknown) => {
        const failure = asError(err);
        if (failure.message?.includes("No matching state")) {
          try {
            const user = await userManager.getUser();
            navigate(user && !user.expired ? returnPath || homePath : "/", { replace: true });
            return;
          } catch (readFailure: unknown) {
            const unreadable = asError(readFailure);
            eventBus.publish("error", { error: unreadable, context: "oidc.callback" });
            setError(unreadable.message || "Authentication failed");
            return;
          }
        }

        eventBus.publish("error", { error: failure, context: "oidc.callback" });
        setError(failure.message || "Authentication failed");
      });
  }, [navigate, getUserManager, homePath]);

  if (error === null) return <>{busyView}</>;
  return <div role="alert">{errorView(error)}</div>;
}
